import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import UserModal from './UserModel';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { useSelector } from 'react-redux';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Users {
  user_id: number;
  user_image: string;
  user_name: string;
  user_address: string;
  user_country: number;
  user_mobile: number;
  user_email: string;
  user_dob: string;
  user_role: number;
  user_status: number;
  user_organization_id?: string | null;
}

const User: React.FC = () => {
  const user = useSelector((state: any) => state.user?.user);
  const [users, setUsers] = useState<Users[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Users | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  console.log('Current Module:', users);
  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchUser = async () => {
    try {
      const data = await apiRequest<Users[]>({
        method: 'get',
        url: 'users',
      });
      setUsers(data);
    } catch (error) {
      showError('Failed to fetch users');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleEditClick = (record: Users) => {
    setIsEditMode(true);
    setInitialValues({
      user_id: record.user_id,
      user_image: record.user_image,
      user_name: record.user_name,
      user_address: record.user_address,
      user_country: record.user_country,
      user_mobile: record.user_mobile,
      user_email: record.user_email,
      user_dob: record.user_dob,
      user_role: record.user_role,
      user_status: record.user_status,
      ...(user?.user_role === 'super-master' && {
        user_organization_id: record.user_organization_id,
      }),
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchUser();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (user_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-user/${user_id}`,
      });
      showSuccess('User deleted');
      fetchUser();
    } catch {
      showError('Failed to delete user');
    }
  };

  const handleStatusToggle = async (subjectId: number) => {
    setUsers((prev) =>
      prev.map((item) =>
        item.user_id === subjectId
          ? { ...item, user_status: item.user_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-user/${subjectId}`,
      });
      showSuccess('User status updated');
    } catch (error) {
      showError('Failed to update status');
      setUsers((prev) =>
        prev.map((item) =>
          item.user_id === subjectId
            ? { ...item, user_status: item.user_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Users>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Users> = [
    {
      title: 'Image',
      dataIndex: 'user_image',
      key: 'user_image',
      width: 150,

      render: (_, record) => (
        <img
          src={`${process.env.REACT_APP_FILE_URL}uploads/users/profile-image/${record.user_image}`}
          alt="User"
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
        />
      ),
    },
    {
      title: 'User Details',
      key: 'user_details',
      dataIndex: 'user_details',
      width: 250,
      render: (_, record) => {
        const dob = new Date(record.user_dob).toLocaleDateString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        return (
          <div>
            <div><strong>Name:</strong> {record.user_name}</div>
            <div><strong>DOB:</strong> {dob}</div>
            <div><strong>user_id:</strong> {record.user_id}</div>
          </div>
        );
      },
      filters: Array.from(new Set(users.map((u) => u.user_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.user_details || null,
      onFilter: (value, record) => record.user_name.includes(value as string),
      sorter: (a, b) => a.user_name.localeCompare(b.user_name),
      sortOrder: sortedInfo.columnKey === 'user_details' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Role',
      dataIndex: 'user_role',
      key: 'user_role',
      width: 100,
      filters: Array.from(new Set(users.map((s) => s.user_role))).map((role) => ({
        text: String(role),
        value: role,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.user_role || null,
      onFilter: (value, record) => String(record.user_role).includes(String(value)),
      sorter: (a, b) => String(a.user_role).localeCompare(String(b.user_role)),
      sortOrder: sortedInfo.columnKey === 'user_role' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.user_role}</div>,
    },
    {
      title: 'Contact Info',
      key: 'contact_info',
      dataIndex: 'contact_info', // optional, just for consistency
      width: 300,
      render: (_, record) => (
        <div>
          <div><strong>Mobile:</strong> {record.user_mobile}</div>
          <div><strong>Email:</strong> {record.user_email}</div>
        </div>
      ),
      filters: Array.from(new Set(users.map((s) => s.user_mobile))).map((mobile) => ({
        text: mobile,
        value: mobile,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.contact_info || null,
      // onFilter: (value, record) => record.user_mobile.includes(value as string),
      // sorter: (a, b) => a.user_mobile.localeCompare(b.user_mobile),
      sortOrder: sortedInfo.columnKey === 'contact_info' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Address',
      key: 'user_full_address',
      dataIndex: 'user_full_address',
      width: 300,

      // Only show Address and Country
      render: (_, record) => (
        <div>
          <div><strong>Address:</strong> {record.user_address}</div>
          <div><strong>Country:</strong> {record.user_country}</div>
        </div>
      ),

      // Filters based on address + country
      filters: Array.from(
        new Set(users.map((s) => `${s.user_address} | ${s.user_country}`))
      ).map((combo) => ({
        text: combo,
        value: combo,
      })),

      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.user_full_address || null,
      onFilter: (value, record) =>
        `${record.user_address} | ${record.user_country}`.includes(value as string),

      // Sort by address + country
      sorter: (a, b) =>
        `${a.user_address} ${a.user_country}`.localeCompare(`${b.user_address} ${b.user_country}`),
      sortOrder: sortedInfo.columnKey === 'user_full_address' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    }


  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Users) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.user_status}
                onChange={() => handleStatusToggle(record.user_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this user?"
              onConfirm={() => handleDelete(record.user_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Users</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add User
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Users>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="user_id"
          dataSource={users.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No user found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, users.length)} of{' '}
                {users.length}
              </span>
              <Pagination
                {...pagination}
                total={users.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <UserModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default User;